import { useAuth0 } from "@auth0/auth0-react";
import { Button, Typography } from "@mui/material";
import { Box } from "@mui/material";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import Qs from "qs";

import { ErrorCard, Header, Wrapper } from "src/components";
import tokens from "src/shared/theme/tokens";
import { createErrorDetailsParamsProps } from "src/shared/models/component/base.model";
import { safeLogout } from "src/shared/utilities/fullStorySession";

/**
 * define the errodetails rendering ui
 */
const ErrorDetailsRow = ({
  title,
  value,
  isLast,
}: {
  title: string;
  value: string;
  isLast?: boolean;
}) => {
  return (
    <Box
      className="content"
      justifyContent={"flex-start"}
      display={"flex"}
      sx={{
        borderTop: `1px solid ${tokens.colors.border}`,
        borderBottom: isLast ? `1px solid ${tokens.colors.border}` : "none",
        py: 3,
      }}
    >
      <Box width={150} pr={2}>
        <Typography
          variant="h2Bold"
          component={"p"}
          color={tokens.colors.inkSecondary}
          textAlign={"right"}
        >
          {`${title} :`}
        </Typography>
      </Box>
      <Box>
        <Typography
          variant="h2"
          component={"p"}
          color={tokens.colors.inkSecondary}
          textAlign={"left"}
        >
          {value}
        </Typography>
      </Box>
    </Box>
  );
};

/**
 * Primary error container screen
 */
export const Error404ApiScreenContainer = () => {
  const { logout, isAuthenticated, isLoading } = useAuth0();
  const location = useLocation(); // get location method from useLocation hook
  const [errorDetails, setErrorDetails] = useState<
    createErrorDetailsParamsProps | undefined
  >(undefined);

  /**
   * Render in intial load based on the dependency array valuses changed
   */
  useEffect(() => {
    // get search from location
    const gerSearchParams = location.search ?? "";

    if (gerSearchParams) {
      // set values from search params __
      const _error = `${Qs.parse(gerSearchParams.substring(1))?.error}`;

      if (_error) {
        setErrorDetails(JSON.parse(_error));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  return (
    <Wrapper>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          minHeight: "calc(var(--vh, 1vh) * 100)",
          background: tokens.colors.pageBg,
        }}
      >
        <Header isBackButtonVisible={false} />
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            paddingX: { xs: 6, sm: 10 },
            paddingBottom: 8,
            textAlign: "center",
          }}
        >
          <ErrorCard>
            <Typography
              variant="h3Bold"
              component={"p"}
              color={tokens.colors.primary}
              textAlign={"center"}
              marginBottom={6}
            >
              {"Something went wrong. Here are the details information:"}
            </Typography>

            {/* Start . display the error details */}
            {errorDetails !== undefined && (
              <Box display={"flex"} flexDirection={"column"} marginBottom={6}>
                <ErrorDetailsRow
                  title="Error Message"
                  value={errorDetails.errorMessage}
                />
                {/* End . Row */}
                <ErrorDetailsRow
                  title="Target API"
                  value={errorDetails.targetAPI}
                />
                {/* End . Row */}
                <ErrorDetailsRow
                  title="API Method"
                  value={errorDetails.method}
                />
                {/* End . Row */}
                <ErrorDetailsRow
                  title="RTK Endpoint Name"
                  value={errorDetails.endpointName}
                  isLast
                />
                {/* End . Row */}
              </Box>
            )}
            {/* End . display the error details */}

            <Button
              variant="contained"
              color="primary"
              type="button"
              sx={{ width: "100px", marginX: "auto" }}
              onClick={() => safeLogout(logout)}
            >
              {isAuthenticated && !isLoading && "Log Out"}
              {!isAuthenticated && !isLoading && "Home"}
            </Button>
          </ErrorCard>
        </Box>
      </Box>
    </Wrapper>
  );
};
